import React, { useEffect, useMemo, useState } from "react";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import Select from "../../Components/Select";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchOnboardingInvitations,
  isExistingNeverLoggedInConflict,
  sendExistingUserPortfolioInvite,
  sendExistingUserWelcomeInvite,
  sendOnboardingInvitation,
  type ExistingUserInviteConflict,
} from "../../Store/Reducers/EasyOnboardingSlice";
import { fetchVendorAdminGrades } from "../../Store/Reducers/VendorAdminSlice";
import { isValidEmail } from "../../Utils/Constants/RegexConstants";
import {
  ACCOUNT_TEAM_ALLOWED_ROLES,
  ACCOUNT_TEAM_ROLES,
  ONBOARDING_IDENTITY,
  type OnboardingIdentity,
} from "../../Utils/Constants/RoleConstants";

interface SendOnboardingInviteModalProps {
  isOpen: boolean;
  toggle: () => void;
}

const ROLE_OPTIONS = [
  { value: ONBOARDING_IDENTITY.ACCOUNT_TEAM, label: "Account Team" },
  { value: ONBOARDING_IDENTITY.C_LEVEL, label: "C-Level" },
  { value: ONBOARDING_IDENTITY.EXECUTIVE, label: "Executive" },
];

const ACCOUNT_TEAM_ROLE_SELECT_OPTIONS = ACCOUNT_TEAM_ALLOWED_ROLES.map(
  role => ({
    label: role,
    value: role,
  })
);

const SendOnboardingInviteModal: React.FC<SendOnboardingInviteModalProps> = ({
  isOpen,
  toggle,
}) => {
  const dispatch = useAppDispatch();
  const { sendInviteLoading, sendWelcomeLoading, sendPortfolioLoading } =
    useAppSelector(state => state.easyOnboarding);
  const { grades } = useAppSelector(state => state.vendorAdmin);

  const [invitedEmail, setInvitedEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [invitedName, setInvitedName] = useState("");
  const [invitedTitle, setInvitedTitle] = useState("");
  const [companyIdentity, setCompanyIdentity] = useState<OnboardingIdentity>(
    ONBOARDING_IDENTITY.ACCOUNT_TEAM
  );
  const [teamRole, setTeamRole] = useState<string>(ACCOUNT_TEAM_ROLES.AE);
  const [invitedGrade, setInvitedGrade] = useState<string>("");
  const [existingUserConflict, setExistingUserConflict] =
    useState<ExistingUserInviteConflict | null>(null);

  const isAccountTeam = companyIdentity === ONBOARDING_IDENTITY.ACCOUNT_TEAM;

  const gradeOptions = useMemo(() => {
    if (!grades?.items || !Array.isArray(grades.items)) return [];
    return grades.items.map((item: { grade: number; grade_name: string }) => ({
      label: item.grade_name,
      value: String(item.grade),
    }));
  }, [grades]);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchVendorAdminGrades());
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setInvitedEmail("");
    setEmailError("");
    setInvitedName("");
    setInvitedTitle("");
    setCompanyIdentity(ONBOARDING_IDENTITY.ACCOUNT_TEAM);
    setTeamRole(ACCOUNT_TEAM_ROLES.AE);
    setInvitedGrade("");
    setExistingUserConflict(null);
  }, [isOpen]);

  useEffect(() => {
    if (gradeOptions.length > 0 && !invitedGrade) {
      setInvitedGrade(gradeOptions[0].value);
    }
  }, [gradeOptions, invitedGrade]);

  const isValid = useMemo(() => {
    if (
      !invitedEmail.trim() ||
      !isValidEmail(invitedEmail) ||
      !invitedName.trim() ||
      !invitedTitle.trim()
    ) {
      return false;
    }
    if (isAccountTeam && !teamRole) {
      return false;
    }
    if (!invitedGrade) {
      return false;
    }
    return true;
  }, [
    invitedEmail,
    invitedName,
    invitedTitle,
    isAccountTeam,
    teamRole,
    invitedGrade,
  ]);

  const handleSubmit = async () => {
    if (!isValid) return;

    const payload = {
      invited_email: invitedEmail.trim(),
      invited_name: invitedName.trim(),
      invited_title: invitedTitle.trim(),
      company_identity_hint: companyIdentity,
      invited_grade: Number(invitedGrade),
      ...(isAccountTeam ? { team_role_hint: teamRole } : {}),
    };

    try {
      await dispatch(sendOnboardingInvitation(payload)).unwrap();
      toggle();
      dispatch(fetchOnboardingInvitations());
    } catch (error) {
      const payload = (error as { payload?: unknown })?.payload ?? error;
      if (isExistingNeverLoggedInConflict(payload)) {
        setExistingUserConflict(payload);
      }
    }
  };

  const existingUserEmailLoading = sendWelcomeLoading || sendPortfolioLoading;

  const handleSendWelcomeOnly = async () => {
    if (!existingUserConflict?.user_id) return;
    try {
      await dispatch(
        sendExistingUserWelcomeInvite({
          user_id: existingUserConflict.user_id,
        })
      ).unwrap();
      setExistingUserConflict(null);
      toggle();
    } catch {
      // Error toast handled in slice
    }
  };

  const handleSendPortfolioInvite = async () => {
    if (!existingUserConflict?.user_id) return;
    try {
      await dispatch(
        sendExistingUserPortfolioInvite({
          user_id: existingUserConflict.user_id,
        })
      ).unwrap();
      setExistingUserConflict(null);
      toggle();
    } catch {
      // Error toast handled in slice
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title="Send onboarding invite"
      size="lg"
    >
      <div className="space-y-4">
        <CommonInput
          label="Invitee email"
          name="invited_email"
          type="email"
          value={invitedEmail}
          onChange={e => {
            const value = e.target.value;
            setInvitedEmail(value);
            if (value.trim() === "") {
              setEmailError("");
            } else if (!isValidEmail(value)) {
              setEmailError("Please enter a valid email address");
            } else {
              setEmailError("");
            }
          }}
          placeholder="name@company.com"
          required
          error={emailError}
        />

        <CommonInput
          label="Invitee name"
          name="invited_name"
          value={invitedName}
          onChange={e => setInvitedName(e.target.value)}
          placeholder="Full name"
          required
        />

        <CommonInput
          label="Invitee title"
          name="invited_title"
          value={invitedTitle}
          onChange={e => setInvitedTitle(e.target.value)}
          placeholder="Title"
          required
        />

        <Select
          label="Grade"
          name="invited_grade"
          value={invitedGrade}
          onChange={e => setInvitedGrade(e.target.value)}
          options={gradeOptions}
          isShowPlaceHolder={gradeOptions.length === 0}
          placeholder="Loading grades..."
          disabled={gradeOptions.length === 0}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Role"
            name="company_identity"
            value={companyIdentity}
            onChange={e =>
              setCompanyIdentity(e.target.value as OnboardingIdentity)
            }
            options={ROLE_OPTIONS}
            isShowPlaceHolder={false}
            className={isAccountTeam ? "" : "md:col-span-2"}
            required
          />

          {isAccountTeam && (
            <Select
              label="Role Type"
              name="team_role"
              value={teamRole}
              onChange={e => setTeamRole(e.target.value)}
              options={ACCOUNT_TEAM_ROLE_SELECT_OPTIONS}
              isShowPlaceHolder={false}
              required
            />
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button
            variant="secondary"
            onClick={toggle}
            disabled={
              sendInviteLoading || sendWelcomeLoading || sendPortfolioLoading
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={sendInviteLoading}
            disabled={
              !isValid ||
              sendInviteLoading ||
              sendWelcomeLoading ||
              sendPortfolioLoading
            }
          >
            Send invite
          </Button>
        </div>
      </div>

      <CommonModal
        isOpen={Boolean(existingUserConflict)}
        toggle={() => setExistingUserConflict(null)}
        title="User already in the system"
        size="md"
        nested
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button
              variant="primary"
              onClick={handleSendPortfolioInvite}
              loading={sendPortfolioLoading}
              disabled={existingUserEmailLoading}
              className="w-full"
            >
              Send Welcome & Portfolio Accounts
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleSendWelcomeOnly}
              loading={sendWelcomeLoading}
              disabled={existingUserEmailLoading}
              className="w-full"
            >
              Send Welcome only
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-gray-700">
            <span className="font-semibold text-gray-900">
              {existingUserConflict?.full_name ||
                existingUserConflict?.email ||
                "This person"}
            </span>{" "}
            is already in the system with the{" "}
            <span className="font-semibold text-gray-900">
              {existingUserConflict?.role || "Unknown"}
            </span>{" "}
            role and hasn&apos;t logged in yet.
          </p>
          <p className="text-xs leading-relaxed text-gray-500">
            Please select whether to send the welcome email only, or to send the
            welcome email together with portfolio account details for associated
            accounts.
          </p>
        </div>
      </CommonModal>
    </CommonModal>
  );
};

export default SendOnboardingInviteModal;
